import { AxiosResponse } from 'axios';
import {
  CreatePricingAgreementInput,
  Order,
  OrderShop,
  OrderShopFilters,
  OrderShopListItem,
  PaginatedInput,
  PaginatedResponse,
  PatchPricingAgreementInput,
  PriceAgreement,
  PricingAgreement
} from 'noddi-types';
import { apiVersion, FetchMethods, ResponseType } from './commonResourceTypes';
import { CommonAxiosResType, getNoddiUrl } from './resources';

export const PartnerURLKeys = {
  getPaginatedOrderShop: 'getPaginatedOrderShop',
  getPaginatedOrderBookings: 'getPaginatedOrderBookings',
  getPaginatedPricingAgreements: 'getPaginatedPricingAgreements',
  getOrderShop: 'getOrderShop',
  postCreatePricingAgreement: 'postCreatePricingAgreement',
  patchPricingAgreement: 'patchPricingAgreement',
  getPricingAgreement: 'getPricingAgreement',
  deletePricingAgreement: 'deletePricingAgreement'
} as const;

export type PartnerURLKeysType = keyof typeof PartnerURLKeys;

type InputDataOrVoid<Type extends PartnerURLKeysType> = InputData<Type> extends void ? void : InputData<Type>;

type InputData<urlKey extends PartnerURLKeysType> = PartnerServerRequests[urlKey]['input'];

export const getPartnerUrls: {
  [key in PartnerURLKeysType]: {
    getUrl: (input: InputDataOrVoid<key>) => string;
    getBody?: (input: InputDataOrVoid<key>) => Partial<InputData<key>>;
    method: FetchMethods;
    responseType?: ResponseType;
    handleRes?: (res: CommonAxiosResType<key>) => Promise<unknown>;
    getHeaders?: (input: InputDataOrVoid<key>) => Record<string, string>;
    useSuperUserToken?: boolean;
    skipConvertingToCamelCase?: boolean;
  };
} = {
  getPaginatedOrderShop: {
    getUrl(queryParams) {
      return getNoddiUrl({
        path: `${apiVersion}orders-shop/`,
        queryParams
      });
    },
    method: FetchMethods.get
  },
  getOrderShop: {
    getUrl(queryParams) {
      return getNoddiUrl({
        path: `${apiVersion}orders-shop/${queryParams.orderId}/`
      });
    },
    method: FetchMethods.get
  },
  postCreatePricingAgreement: {
    getUrl: () =>
      getNoddiUrl({
        path: `${apiVersion}user-group-pricing-agreements/`
      }),
    getBody: (data) => data,
    method: FetchMethods.post
  },
  getPaginatedPricingAgreements: {
    getUrl(queryParams) {
      return getNoddiUrl({
        path: `${apiVersion}user-group-pricing-agreements/`,
        queryParams
      });
    },
    method: FetchMethods.get
  },
  getPaginatedOrderBookings: {
    getUrl(queryParams) {
      return getNoddiUrl({
        path: `${apiVersion}orders/`,
        queryParams
      });
    },
    method: FetchMethods.get
  },
  getPricingAgreement: {
    getUrl: ({ id }) =>
      getNoddiUrl({
        path: `${apiVersion}user-group-pricing-agreements/${id}/`
      }),
    method: FetchMethods.get
  },
  deletePricingAgreement: {
    getUrl: ({ id }) =>
      getNoddiUrl({
        path: `${apiVersion}user-group-pricing-agreements/${id}/`
      }),
    method: FetchMethods.delete
  },
  patchPricingAgreement: {
    getUrl: ({ id }) =>
      getNoddiUrl({
        path: `${apiVersion}user-group-pricing-agreements/${id}/`
      }),
    getBody: (data) => data,
    method: FetchMethods.patch
  }
} as const;

export interface PartnerServerRequests {
  getPaginatedOrderShop: {
    input: PaginatedInput & OrderShopFilters;
    output: PaginatedResponse<OrderShopListItem[]>;
  };
  getPaginatedOrderBookings: {
    input: PaginatedInput & OrderShopFilters;
    output: PaginatedResponse<Order[]>;
  };
  getPricingAgreement: {
    input: { id: number };
    output: PricingAgreement;
  };
  deletePricingAgreement: {
    input: { id: number };
    output: void;
  };
  getOrderShop: {
    input: {
      orderId: number;
    };
    output: OrderShop;
  };
  getPaginatedPricingAgreements: {
    input: PaginatedInput & {
      userGroupIds?: number[];
      scope?: PriceAgreement[];
    };
    output: PaginatedResponse<PricingAgreement[]>;
  };
  postCreatePricingAgreement: {
    input: CreatePricingAgreementInput;
    output: AxiosResponse<{ id: string }>;
  };
  patchPricingAgreement: {
    input: PatchPricingAgreementInput;
    output: void;
  };
}
